import { useRouter, type Router, type RouteLocationRaw } from 'vue-router';
import { setActivePinia } from 'pinia';

import {
	beginKeycloakLogout,
	clientDebugLog,
	endKeycloakLogout,
	performKeycloakLogout,
	fcmToken,
} from '@apexx/auth';
import {
	clearSoftUnauthorizedCircuit,
	recordSoftUnauthorizedAttempt,
} from '@apexx/shell-contract';

import { clearLastIdentity, getLastIdentity } from '@/auth/lastIdentity';
import { shellOrchestrator } from '@/shell/orchestrator';
import { clearLastHubAppId } from '@/shell/lastHubApp';
import { normalizeHubTheme } from '@/shell/theme';
import { hubPinia } from '@/pinia';
import { useHubAuthStore } from '@/store/hubAuthStore';

export type HubLogoutReason =
	| 'user-sign-out'
	| 'api-unauthorized'
	| 'api-unauthorized-escalated'
	| 'session-expired'
	| 'native-logout-return'
	| (string & {});

export type HubLogoutRedirect = 'login' | 'reauth';

export type PerformHubLogoutOptions = {
	/**
	 * When false, only clears local tokens (preserves Keycloak SSO cookie for silent re-auth).
	 * Defaults to true for intentional sign-out.
	 */
	endSsoSession?: boolean;
	/** Why logout ran — included in console logs for field debugging. */
	reason?: HubLogoutReason;
	/**
	 * Where to land after teardown.
	 * Soft unauthorized / session expiry → `reauth` (falls back to login if no identity).
	 * Intentional sign-out → `login`.
	 */
	redirectTo?: HubLogoutRedirect;
	/**
	 * When true, wipes the persisted last-identity snapshot.
	 * Soft unauthorized must leave this false so the re-auth screen can Continue as…
	 */
	clearIdentity?: boolean;
};

function logHubLogout(message: string, details?: Record<string, unknown>): void {
	clientDebugLog('info', `[hub:logout] ${message}`, details);
}

function softUnauthorizedStorage(): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null {
	try {
		if (typeof sessionStorage === 'undefined') {
			return null;
		}

		return sessionStorage;
	} catch {
		return null;
	}
}

/** Resolves post-logout navigation: reauth when a stored identity exists, else login. */
export async function resolveSignedOutRedirect(
	preferred: HubLogoutRedirect = 'reauth',
): Promise<RouteLocationRaw> {
	if (preferred === 'login') {
		return { name: 'login' };
	}

	const identity = await getLastIdentity();
	return identity ? { name: 'reauth' } : { name: 'login' };
}

/**
 * Clears hub bearer state and optionally ends the Keycloak SSO session.
 * Use {@link performHubUnauthorizedSession} for API 401 / soft session failures.
 */
export async function performHubLogout(
	router: Router,
	options: PerformHubLogoutOptions = {},
): Promise<void> {
	const endSsoSession = options.endSsoSession ?? true;
	const reason = options.reason ?? (endSsoSession ? 'user-sign-out' : 'local-session-clear');
	const redirectTo = options.redirectTo ?? (endSsoSession ? 'login' : 'reauth');
	// Soft unauthorized (incl. escalate → reauth) keeps identity. Intentional sign-out wipes it.
	const shouldClearIdentity =
		options.clearIdentity ?? (endSsoSession && redirectTo === 'login');

	logHubLogout('starting', {
		reason,
		endSsoSession,
		redirectTo,
		clearIdentity: shouldClearIdentity,
	});
	// Embedded apps (chat/machines) have their own Pinia. useHubAuthStore() without an
	// explicit instance would clear a different store and leave the hub "logged in",
	// so the login guard redirects to the launcher.
	setActivePinia(hubPinia);
	await beginKeycloakLogout();

	try {
		await clearLastHubAppId();

		if (shouldClearIdentity) {
			await clearLastIdentity();
		}

		if (endSsoSession) {
			await fcmToken.unregister().catch(() => {});
			const storage = softUnauthorizedStorage();
			if (storage) {
				clearSoftUnauthorizedCircuit(storage);
			}
		}

		const authStore = useHubAuthStore(hubPinia);
		authStore.clearAccessToken();

		await shellOrchestrator.unmountActive().catch(() => {});

		// Always leave the embedded app-shell route before Keycloak logout. On native,
		// performKeycloakLogout opens the system browser and returns `redirected` without
		// navigating — staying on /apps/:appId leaves the h-dvh shell container active and
		// login renders shrunk after the post_logout deep link (in.rabatteries.gold://login).
		const destination = await resolveSignedOutRedirect(redirectTo);
		await router.replace(destination);
		normalizeHubTheme();

		try {
			const result = await performKeycloakLogout({ endSsoSession });
			logHubLogout('keycloak clear finished', { reason, endSsoSession, result });
		} catch (error) {
			logHubLogout('keycloak clear failed; local auth already cleared', {
				reason,
				endSsoSession,
				error,
			});
		}

		// Re-clear in case an in-flight refresh re-applied the hub bearer before drain completed.
		authStore.clearAccessToken();
	} finally {
		endKeycloakLogout();
		logHubLogout('finished', { reason, endSsoSession, redirectTo });
	}
}

/**
 * Soft session teardown for API 401 / expired bearer while Keycloak SSO may still be valid.
 * Clears local hub state, forgets last app, and routes to reauth without wiping identity.
 * Escalates to full SSO logout when soft unauthorized repeats within a short window
 * (avoids silent SSO → same 401 → soft logout loops) — identity still survives.
 */
export async function performHubUnauthorizedSession(
	router: Router,
	reason: HubLogoutReason = 'api-unauthorized',
): Promise<void> {
	const storage = softUnauthorizedStorage();
	const decision = storage
		? recordSoftUnauthorizedAttempt(storage)
		: { escalate: false, count: 1 };

	if (decision.escalate) {
		logHubLogout('escalating soft unauthorized to full SSO', {
			reason: 'api-unauthorized-escalated',
			count: decision.count,
			priorReason: reason,
		});
		if (storage) {
			clearSoftUnauthorizedCircuit(storage);
		}
		await performHubLogout(router, {
			endSsoSession: true,
			reason: 'api-unauthorized-escalated',
			redirectTo: 'reauth',
			clearIdentity: false,
		});
		return;
	}

	logHubLogout('soft unauthorized (SSO preserved)', {
		reason,
		count: decision.count,
		endSsoSession: false,
	});
	await performHubLogout(router, {
		endSsoSession: false,
		reason,
		redirectTo: 'reauth',
		clearIdentity: false,
	});
}

/** Same as {@link performHubLogout} but uses the current router from vue-router. */
export async function performHubLogoutFromCurrentRoute(
	options: PerformHubLogoutOptions = {},
): Promise<void> {
	await performHubLogout(useRouter(), options);
}
